import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthError } from '@supabase/supabase-js'
import { useAuth } from './hooks/useAuth'
import { useCompany } from './contexts/CompanyContext'
import { OnboardingModal } from './components/OnboardingModal'
import { InactivityWarningModal } from './components/InactivityWarningModal'
import { MainLayout } from './components/layout/MainLayout'
import { PaymentRequired } from './components/PaymentRequired'
import { Button } from './components/ui/Button'

// Lazy load all pages for code splitting
const Landing = lazy(() => import('./pages/Landing').then(m => ({ default: m.Landing })))
const Signup = lazy(() => import('./pages/Signup').then(m => ({ default: m.Signup })))
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })))
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })))
const Customers = lazy(() => import('./pages/Customers').then(m => ({ default: m.Customers })))
const Jobs = lazy(() => import('./pages/Jobs').then(m => ({ default: m.Jobs })))
const Invoices = lazy(() => import('./pages/Invoices').then(m => ({ default: m.Invoices })))
const Quotes = lazy(() => import('./pages/Quotes').then(m => ({ default: m.Quotes })))
const Routing = lazy(() => import('./pages/Routing').then(m => ({ default: m.Routing })))
const Messages = lazy(() => import('./pages/Messages').then(m => ({ default: m.Messages })))
const Reports = lazy(() => import('./pages/Reports').then(m => ({ default: m.Reports })))
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })))
const CompanySettingsPage = lazy(() => import('./pages/CompanySettings').then(m => ({ default: m.CompanySettingsPage })))
const TeamManagement = lazy(() => import('./pages/TeamManagement').then(m => ({ default: m.TeamManagement })))
const NewCustomerForm = lazy(() => import('./pages/NewCustomerForm').then(m => ({ default: m.NewCustomerForm })))
const ExistingCustomerForm = lazy(() => import('./pages/ExistingCustomerForm'))
const NewCustomerSubmissions = lazy(() => import('./pages/NewCustomerSubmissions'))
const ExistingCustomerUpdatesList = lazy(() => import('./pages/ExistingCustomerUpdatesList'))
const CustomerPortalLanding = lazy(() => import('./pages/CustomerPortalLanding').then(m => ({ default: m.CustomerPortalLanding })))
const ExistingCustomerLookup = lazy(() => import('./pages/ExistingCustomerLookup').then(m => ({ default: m.ExistingCustomerLookup })))
const ExistingCustomerUpdateForm = lazy(() => import('./pages/ExistingCustomerUpdateForm').then(m => ({ default: m.ExistingCustomerUpdateForm })))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })))
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({ default: m.TermsOfService })))
const HelpCenter = lazy(() => import('./pages/HelpCenter').then(m => ({ default: m.HelpCenter })))
const Documentation = lazy(() => import('./pages/Documentation').then(m => ({ default: m.Documentation })))
const APIReference = lazy(() => import('./pages/APIReference').then(m => ({ default: m.APIReference })))
const InvoicePayment = lazy(() => import('./pages/InvoicePayment').then(m => ({ default: m.InvoicePayment })))
const QuoteView = lazy(() => import('./pages/QuoteView').then(m => ({ default: m.QuoteView })))
const QuickBooksCallback = lazy(() => import('./pages/QuickBooksCallback').then(m => ({ default: m.QuickBooksCallback })))
const SignupPayment = lazy(() => import('./pages/SignupPayment').then(m => ({ default: m.SignupPayment })))
const SignupComplete = lazy(() => import('./pages/SignupComplete').then(m => ({ default: m.SignupComplete })))

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-primary)] mx-auto mb-4"></div>
      <p className="text-sm text-muted">Loading...</p>
    </div>
  </div>
)

const App: React.FC = () => {
  const { user, loading, role, signOut, isSuperAdmin } = useAuth()
  const { currentCompany, isLoading: companyLoading, error: companyError } = useCompany()
  const location = useLocation()

  const allowedRoles = new Set(['owner', 'admin', 'manager'])
  const hasRoleAccess = isSuperAdmin || (role ? allowedRoles.has(role) : false)

  // Public routes that don't require auth
  const publicRoutes = [
    '/signup',
    '/new-customer',
    '/existing-customer',
    '/privacy',
    '/terms',
    '/help',
    '/docs',
    '/api',
    '/pay',
    '/quote',
    '/integrations/quickbooks/callback'
  ]

  // Check for company-specific public routes (customer portal)
  const isCompanyPublicRoute = /^\/(customer-portal|existing-customer-lookup|customer-update)/.test(location.pathname)

  // Check if current path is a public route
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route)) || isCompanyPublicRoute

  // Check if we're on a company subdomain
  const hostname = window.location.hostname
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  const parts = hostname.split('.')
  const hasSubdomain = parts.length >= 3 && parts[0] !== 'app' && parts[0] !== 'www'

  // If on a subdomain but no valid company found (and not loading), redirect to login
  const isInvalidSubdomain = hasSubdomain && !companyLoading && !currentCompany && !isLocalhost

  // Only show loading screen for initial auth check, not for company refetches
  if (loading && !isPublicRoute) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--color-surface-muted)] text-strong">
        <img
          src="/brand/sergio-logo.png"
          alt="Sergio"
          className="h-16 w-16 rounded-full border-2 border-subtle shadow-card mb-6"
        />
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--color-primary)]"></div>
        <p className="mt-4 text-sm text-muted">Preparing your workspace...</p>
      </div>
    )
  }

  // Show onboarding modal if user is logged in but hasn't completed onboarding
  // Skip onboarding for super admin (god account)
  const needsOnboarding = user && !isPublicRoute && currentCompany && !currentCompany.onboarding_complete && !companyLoading && !isSuperAdmin

  return (
    <div data-app-loaded="true">
      {needsOnboarding && <OnboardingModal />}
      {user && <InactivityWarningModal />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
        {/* Landing Page - Public marketing site */}
        <Route
          path="/"
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : isInvalidSubdomain ? (
              <Navigate to="/login" replace />
            ) : (
              <Landing />
            )
          }
        />

        {/* Signup Flow - Public */}
        <Route path="/signup" element={user ? <Navigate to="/dashboard" replace /> : <Signup />} />
        <Route path="/signup/payment" element={<SignupPayment />} />
        <Route path="/signup/complete" element={<SignupComplete />} />

        {/* Login - Public */}
        <Route
          path="/login"
          element={user ? <Navigate to="/dashboard" replace /> : <Login />}
        />

        {/* Public customer forms */}
        <Route path="/new-customer" element={<NewCustomerForm />} />
        <Route path="/existing-customer" element={<ExistingCustomerForm />} />

        {/* Public customer portal - works on subdomains (company.sergio.app) or main domain (sergio.app) */}
        <Route path="/customer-portal" element={<CustomerPortalLanding />} />
        <Route path="/existing-customer-lookup" element={<ExistingCustomerLookup />} />
        <Route path="/customer-update/:token" element={<ExistingCustomerUpdateForm />} />

        {/* Legacy route support for /:companySlug/customer-portal */}
        <Route path="/:companySlug/customer-portal" element={<Navigate to="/customer-portal" replace />} />
        <Route path="/:companySlug/existing-customer-lookup" element={<Navigate to="/existing-customer-lookup" replace />} />
        <Route path="/:companySlug/customer-update/:token" element={<Navigate to="/customer-update/:token" replace />} />

        {/* Legal pages */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* Documentation pages */}
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/docs" element={<Documentation />} />
        <Route path="/api" element={<APIReference />} />

        {/* Public invoice payment page - no auth required */}
        {/* SECURITY: Uses payment_token (UUID) instead of invoice ID to prevent enumeration */}
        <Route path="/pay/:paymentToken" element={<InvoicePayment />} />

        {/* Public quote view page - no auth required */}
        {/* SECURITY: Uses quote_token (UUID) instead of quote ID to prevent enumeration */}
        <Route path="/quote/:quoteToken" element={<QuoteView />} />

        {/* QuickBooks OAuth callback - opened in popup window */}
        <Route path="/integrations/quickbooks/callback" element={<QuickBooksCallback />} />

        {/* Authenticated app routes */}
        <Route
          element={
            user ? (
              hasRoleAccess ? (
                <MainLayout />
              ) : (
                <UnauthorizedScreen onSignOut={signOut} />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          {/* All authenticated routes - cancelled/expired users redirected to billing in MainLayout */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/quotes" element={<Quotes />} />
          <Route path="/routing" element={<Routing />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/company" element={<CompanySettingsPage />} />
          <Route path="/settings/team" element={<TeamManagement />} />
          <Route path="/data/new-submissions" element={<NewCustomerSubmissions />} />
          <Route path="/data/existing-updates" element={<ExistingCustomerUpdatesList />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  )
}

export default App

interface UnauthorizedScreenProps {
  onSignOut: () => Promise<{ error: AuthError | null }>
}

const UnauthorizedScreen: React.FC<UnauthorizedScreenProps> = ({ onSignOut }) => {
  const handleSignOut = async () => {
    // signOut now handles the redirect internally
    await onSignOut()
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-surface-50 px-6 text-center">
      <div className="max-w-md space-y-4">
        <h1 className="text-2xl font-semibold text-slate-900">Access Restricted</h1>
        <p className="text-slate-600">
          Your account does not have permission to access the Sergio dashboard. If you believe this is an error,
          contact your administrator.
        </p>
        <Button onClick={handleSignOut} className="w-full">
          Return to Login
        </Button>
      </div>
    </div>
  )
}
